// Small pieces shared by the Program Editor modal's tabs, in the board's dark
// style (the #1C1C1C panel on a #2A2A2A hairline, the orange underline).
// Ported from Ronan's capability prototype (prototype/capability-mode, ui.tsx).

import React from 'react';
import { Box, Typography } from '@mui/material';
import { elliottTokens as t } from '../add-program-chat/tokens';
import { capabilityColor } from '../teamCapability';

export { t };

export const ORANGE = '#F79420';

export const Panel: React.FC<{ children: React.ReactNode; sx?: object }> = ({ children, sx }) => (
  <Box sx={{ bgcolor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', p: 2, ...sx }}>
    {children}
  </Box>
);

export const Muted: React.FC<{ children: React.ReactNode; size?: number }> = ({ children, size = 13 }) => (
  <Typography sx={{ fontSize: size, color: t.textSecondary, lineHeight: 1.5 }}>{children}</Typography>
);

/** Small uppercase label above a field or a column. */
export const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: t.textTertiary }}
  >
    {children}
  </Typography>
);

/** A plain progress bar, in the capability scores' red-to-green colours. */
export const Bar: React.FC<{ percent: number | null; height?: number }> = ({ percent, height = 6 }) => (
  <Box sx={{ width: '100%', height, borderRadius: height / 2, bgcolor: '#2A2A2A', overflow: 'hidden', flexShrink: 0 }}>
    <Box
      sx={{
        width: `${Math.max(0, Math.min(100, percent ?? 0))}%`,
        height: '100%',
        borderRadius: height / 2,
        bgcolor: capabilityColor(percent ?? 0)
      }}
    />
  </Box>
);

/** Tabs in the dashboard's section-title style: white active, dim inactive, orange underline. */
export const TitleTabs: React.FC<{
  tabs: { key: string; label: string }[];
  active: string;
  onTab: (key: string) => void;
}> = ({ tabs, active, onTab }) => (
  <Box role="tablist" sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
    {tabs.map((tab, i) => (
      <React.Fragment key={tab.key}>
        {i > 0 && <Box sx={{ width: '1px', height: 18, bgcolor: 'rgba(247, 148, 32, 0.4)' }} />}
        <Box
          component="button"
          type="button"
          role="tab"
          aria-selected={tab.key === active}
          onClick={() => onTab(tab.key)}
          sx={{
            p: 0,
            pb: '3px',
            border: 'none',
            borderBottom: `2px solid ${tab.key === active ? ORANGE : 'transparent'}`,
            bgcolor: 'transparent',
            cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
            fontSize: 14,
            fontWeight: 700,
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            color: tab.key === active ? t.textPrimary : '#565656',
            '&:hover': { color: tab.key === active ? t.textPrimary : t.textSecondary }
          }}
        >
          {tab.label}
        </Box>
      </React.Fragment>
    ))}
  </Box>
);
